import { useState } from "react";
import { useNavigate } from "react-router-dom";
import TopBar from "@/components/top-bar";
import Icon from "@/components/icon";
import LoginRequired from "@/components/login-required";
import Section from "@/components/section";
import Row, { RowDivider } from "@/components/row";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer";
import { generateGradientFromId } from "@/utils/gradient";
import { useProfileStore } from "@/store/profile-store";
import { useLogout } from "@/features/auth/hooks/use-logout";
import { APP_VERSION } from "@/version";
import {
  EITAA_BASE_URL,
  SUPPORT_EITAA_USERNAME,
  SUPPORT_PHONE,
} from "@/utils/constant";
import { openUrl } from "@/utils/utilities";

const Chevron = () => (
  <Icon name="chevron-left" size={18} className="text-default-300" />
);

export default function ProfilePage() {
  const navigate = useNavigate();
  const { profile, tokens } = useProfileStore();

  const { logout, loggingOut, canLogout } = useLogout();
  const [logoutOpen, setLogoutOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    setLogoutOpen(false);
  };

  const openSupportChat = () => {
    const username = SUPPORT_EITAA_USERNAME.replace(/^@/, "");
    openUrl(`${EITAA_BASE_URL}/${username}`);
  };

  const callSupport = () => {
    const phone = SUPPORT_PHONE.replace(/[^\d+]/g, "");
    window.location.href = `tel:${phone}`;
  };

  if (!tokens?.access_token) {
    return (
      <div className="bg-secondary-background min-h-svh pb-20">
        <TopBar />
        <LoginRequired
          title="برای مشاهده پروفایل وارد شوید"
          description="پس از ورود، اطلاعات حساب، علاقه‌مندی‌ها و تنظیمات شما اینجا نمایش داده می‌شود."
        />
      </div>
    );
  }

  const fullName = [profile?.first_name, profile?.last_name]
    .filter(Boolean)
    .join(" ");
  const initial = (profile?.first_name || "؟").trim().charAt(0);

  return (
    <div className="bg-secondary-background min-h-svh pb-20">
      <TopBar />

      {/* Avatar header */}
      <div className="flex flex-col items-center px-4 pb-2 pt-6">
        <div
          style={{ background: generateGradientFromId(Number(profile?.id) || 1) }}
          className="mb-3 flex size-36 items-center justify-center rounded-[2rem] shadow-card"
        >
          {profile?.avatar ? (
            <img
              className="size-full rounded-[2rem] object-cover"
              src={profile.avatar}
              alt="profile"
              width={160}
              height={160}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <span className="text-3xl font-bold text-white">{initial}</span>
          )}
        </div>

        {fullName ? (
          <h1 className="text-sm font-semibold text-default-900">{fullName}</h1>
        ) : null}
        {profile?.phone ? (
          <h2 className="mt-1 text-sm text-default-500" dir="ltr">
            {profile.phone}
          </h2>
        ) : null}
      </div>

      {/* Account */}
      <Section title="حساب کاربری">
        <Row
          icon="edit-profile"
          iconClassName="bg-blue-500"
          title="ویرایش اطلاعات"
          subtitle="نام، نام خانوادگی و آواتار"
          trailing={<Chevron />}
          onClick={() => navigate("/profile/edit")}
        />
        <RowDivider inset />
        <Row
          icon="heart-fill"
          iconClassName="bg-rose-500"
          title="علاقه‌مندی‌ها"
          subtitle="دوره‌های ذخیره‌شده شما"
          trailing={<Chevron />}
          onClick={() => navigate("/profile/wishlist")}
        />
        <RowDivider inset />
        <Row
          icon="transactions"
          iconClassName="bg-green-500"
          title="تراکنش‌ها"
          subtitle="تاریخچه پرداخت‌ها و خریدها"
          trailing={<Chevron />}
          onClick={() => navigate("/profile/transactions")}
        />
        <RowDivider inset />
        <Row
          icon="setting"
          iconClassName="bg-default-400"
          title="تنظیمات"
          subtitle="اعلان‌ها و تنظیمات برنامه"
          trailing={<Chevron />}
          onClick={() => navigate("/profile/settings")}
        />
      </Section>

      {/* More */}
      <Section title="بیشتر">
        <Row
          icon="phone"
          iconClassName="bg-cyan-500"
          title="اطلاعات دستگاه"
          subtitle="مشخصات مرورگر و دستگاه شما"
          trailing={<Chevron />}
          onClick={() => navigate("/profile/device-info")}
        />
        <RowDivider inset />
        <Row
          icon="term-of-conditions"
          iconClassName="bg-orange-500"
          title="قوانین و مقررات"
          subtitle="شرایط استفاده از خدمات"
          trailing={<Chevron />}
          onClick={() => navigate("/profile/term-of-conditions")}
        />
        <RowDivider inset />
        <Row
          icon="support"
          iconClassName="bg-primary"
          title="ارسال پیام به پشتیبانی"
          subtitle={`@${SUPPORT_EITAA_USERNAME.replace(/^@/, "")}`}
          trailing={<Chevron />}
          onClick={openSupportChat}
        />
        <RowDivider inset />
        <Row
          icon="support-phone"
          iconClassName="bg-green-500"
          title="تماس با پشتیبانی"
          subtitle={<span dir="ltr">{SUPPORT_PHONE}</span>}
          trailing={<Chevron />}
          onClick={callSupport}
        />
      </Section>

      {/* Logout — web only */}
      {canLogout ? (
        <Section>
          <Row
            icon="logout"
            iconClassName="bg-danger"
            title={<span className="font-medium text-danger">خروج از حساب کاربری</span>}
            onClick={() => setLogoutOpen(true)}
          />
        </Section>
      ) : null}


      <Drawer open={logoutOpen} onOpenChange={setLogoutOpen}>
        <DrawerContent>
          <DrawerTitle className="mb-2">خروج از حساب کاربری</DrawerTitle>
          <DrawerDescription className="mb-5">
            آیا مطمئن هستید که می‌خواهید از حساب خود خارج شوید؟
          </DrawerDescription>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setLogoutOpen(false)}
              disabled={loggingOut}
              className="h-11 flex-1 rounded-xl border border-default-200 text-sm font-medium text-default-700 disabled:opacity-60"
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={handleLogout}
              disabled={loggingOut}
              className="h-11 flex-1 rounded-xl bg-danger text-sm font-bold text-white disabled:opacity-60"
            >
              {loggingOut ? "در حال خروج..." : "خروج"}
            </button>
          </div>
        </DrawerContent>
      </Drawer>

      <div className="p-4 text-center text-xs text-default-500 w-full font-mono mb-4" dir="ltr">
        Amoozim Vitrin
        <span className="mx-1 font-mono">
          v{APP_VERSION}
        </span>
      </div>
    </div>
  );
}
